import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Form, Modal, Pagination, Table } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const Project = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState(0);
  const [departmentId, setDepartmentId] = useState(0);
  const [status, setStatus] = useState(1);

  const loadProjects = async () => {
    setLoading(true);
    const res = await API.get('/api/project/?p=0&p_size=1000');
    const { success, message, data } = res.data;
    if (success) {
      setProjects(data || []);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const loadCompanies = async () => {
    const res = await API.get('/api/company/?p=0&p_size=1000');
    const { success, data } = res.data;
    if (success) setCompanies(data || []);
  };

  const loadAllDepartments = async () => {
    const res = await API.get('/api/department/?p=0&p_size=1000');
    const { success, data } = res.data;
    if (success) setAllDepartments(data || []);
  };

  const loadDepartments = async (cid) => {
    if (!cid) { setDepartments([]); return; }
    const res = await API.get(`/api/department/?p=0&p_size=1000&company_id=${cid}`);
    const { success, data } = res.data;
    if (success) setDepartments(data || []);
  };

  useEffect(() => {
    loadProjects();
    loadCompanies();
    loadAllDepartments();
  }, []);

  const companyOptions = companies.map((c) => ({ key: c.id, text: c.name, value: c.id }));
  const deptOptions = departments.map((d) => ({ key: d.id, text: d.name, value: d.id }));
  const getCompanyName = (id) => { const c = companies.find((x) => x.id === id); return c ? c.name : ''; };
  const getDeptName = (id) => { const d = allDepartments.find((x) => x.id === id); return d ? d.name : ''; };

  const openAdd = () => {
    setEditingId(null);
    setName('');
    setCompanyId(0);
    setDepartmentId(0);
    setDepartments([]);
    setStatus(1);
    setShowModal(true);
  };

  const openEdit = (proj) => {
    setEditingId(proj.id);
    setName(proj.name);
    setCompanyId(proj.company_id);
    setDepartmentId(proj.department_id);
    setStatus(proj.status);
    loadDepartments(proj.company_id);
    setShowModal(true);
  };

  const onCompanyChange = (value) => {
    setCompanyId(value);
    setDepartmentId(0);
    setDepartments([]);
    if (value) loadDepartments(value);
  };

  const submit = async () => {
    if (!name.trim()) { showError(t('org.project.name_required')); return; }
    if (!companyId) { showError(t('org.company.select_required')); return; }
    if (!departmentId) { showError(t('org.department.select_required')); return; }
    const payload = { name: name.trim(), company_id: companyId, department_id: departmentId, status };
    let res;
    if (editingId) {
      res = await API.put('/api/project/', { ...payload, id: editingId });
    } else {
      res = await API.post('/api/project/', payload);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(editingId ? t('org.update_success') : t('org.create_success'));
      setShowModal(false);
      loadProjects();
    } else {
      showError(message);
    }
  };

  const onDelete = async (id) => {
    const res = await API.delete(`/api/project/${id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('org.delete_success'));
      loadProjects();
    } else {
      showError(message);
    }
  };

  const onPageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const totalPages = Math.max(1, Math.ceil(projects.length / ITEMS_PER_PAGE));
  const pageData = projects.slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('org.project.title')}</Card.Header>
          <Button size='small' onClick={openAdd} loading={loading}>{t('org.project.add')}</Button>
          <Button size='small' onClick={() => loadProjects()} loading={loading}>{t('org.refresh')}</Button>
          <Table basic='very' compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>{t('org.company.name')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.department.name')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.project.name')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.status')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.created_time')}</Table.HeaderCell>
            <Table.HeaderCell>{t('org.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {pageData.map((proj) => (
            <Table.Row key={proj.id}>
              <Table.Cell>{proj.id}</Table.Cell>
              <Table.Cell>{getCompanyName(proj.company_id)}</Table.Cell>
              <Table.Cell>{getDeptName(proj.department_id)}</Table.Cell>
              <Table.Cell>{proj.name}</Table.Cell>
              <Table.Cell>{proj.status === 1 ? t('org.enabled') : t('org.disabled')}</Table.Cell>
              <Table.Cell>{new Date(proj.created_time * 1000).toLocaleString()}</Table.Cell>
              <Table.Cell>
                <Button size='tiny' onClick={() => openEdit(proj)}>{t('org.edit')}</Button>
                <Button size='tiny' negative onClick={() => onDelete(proj.id)}>{t('org.delete')}</Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
          <Pagination activePage={activePage} onPageChange={onPageChange} size='small' totalPages={totalPages} />
        </Card.Content>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} size='tiny'>
        <Modal.Header>{editingId ? t('org.project.edit') : t('org.project.add')}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Dropdown label={t('org.company.name')} placeholder={t('org.company.select_placeholder')} search selection value={companyId} options={companyOptions} onChange={(e, { value }) => onCompanyChange(value)} />
            <Form.Dropdown label={t('org.department.name')} placeholder={t('org.department.select_placeholder')} search selection value={departmentId} options={deptOptions} onChange={(e, { value }) => setDepartmentId(value)} />
            <Form.Input label={t('org.project.name')} placeholder={t('org.project.name_placeholder')} value={name} onChange={(e, { value }) => setName(value)} />
            <Form.Dropdown label={t('org.status')} selection value={status} options={[{ key: 1, text: t('org.enabled'), value: 1 }, { key: 2, text: t('org.disabled'), value: 2 }]} onChange={(e, { value }) => setStatus(value)} />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowModal(false)}>{t('org.cancel')}</Button>
          <Button positive onClick={submit}>{t('org.save')}</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default Project;
